import * as React from "react";
import { connect } from "react-redux";
import { List, Segment } from "semantic-ui-react";

import { AccordionQuestionnaireSection } from "@teamprevent/common/questionnaires/components/AccordionQuestionnaireSection";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { RadioButton, RadioButtonQuestion } from "@teamprevent/common/questionnaires/components/questions/RadioButtonQuestion";
import { OldDateQuestion } from "@teamprevent/common/questionnaires/components/questions/OldDateQuestion";
import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";
import { ConsultationDetails } from "@teamprevent/common/consultations/models";
import { AnsweredQuestion } from "@teamprevent/common/model";
import { clearQuestionnaire, updateQuestion } from "@teamprevent/common/questionnaires/actions";

import { medicalClassificationSubCategoryOptions, ohaMedicalClassificationOptions } from "../models";
import { Dispatch } from "../../store";
import { loadConsultationDetails } from "../actions";
import { ApplicationState } from "../../applicationState";
import { taskIdSelector } from "../../task/selectors";

import { ClinicalNotesProps, questionnaireKey } from "./ClinicalNotes";

type DispatchProps = {
    loadConsultationDetails: (ohCaseId: string, taskId: string) => Promise<void>;
    updateAnswer: (payload: { sectionId: string; id: string; question: AnsweredQuestion }) => void;
    clearQuestionnaire: () => void;
}

type StateProps = {
    consultationDetails: ConsultationDetails;
    taskId: string;
}

type Props = ClinicalNotesProps & DispatchProps & StateProps;

export const employeeEmailAddressQuestionId = "EmployeeEmailAddress";
export const managerEmailAddressQuestionId = "ManagerEmailAddress";
export const hrEmailAddressQuestionId = "HREmailAddress";

export const consentToReleaseReportSectionId = "Completion";

class OldReferralClinicalNotesSectionsUnconnected extends React.Component<Props> {
    constructor(props: Props) {
        super(props);

        props.clearQuestionnaire();
    }

    public async componentDidMount() {
        if (this.props.taskId && this.props.ohCase?.id) {
            await this.props.loadConsultationDetails(this.props.ohCase.id, this.props.taskId);
        }
    }

    public componentDidUpdate(prevProps: Props) {
        if (this.props.taskId && this.props.taskId !== prevProps.taskId &&
            this.props.ohCase?.id && this.props.ohCase.id !== prevProps.ohCase?.id) {
            this.props.loadConsultationDetails(this.props.ohCase.id, this.props.taskId);
        }
    }

    public componentWillUnmount() {
        this.props.clearQuestionnaire();
    }

    public render() {
        return (
            <>
                <AccordionQuestionnaireSection
                    title="Clinical Notes"
                    id="ClinicalNotes"
                    index={this.props.index}
                >
                    <TextQuestion
                        inline
                        readonly={this.props.readonly}
                        questionConfig={({ id: "ReasonForReferral", questionText: "Reason for referral" })}
                        multiline
                    />
                    <TextQuestion
                        inline
                        readonly={this.props.readonly}
                        questionConfig={({ id: "CurrentRoleDuties", questionText: "Current job role and duties" })}
                        multiline
                    />
                    <TextQuestion
                        inline
                        readonly={this.props.readonly}
                        questionConfig={({ id: "EmploymentHistory", questionText: "Employment history" })}
                        multiline
                    />
                    <TextQuestion
                        inline
                        readonly={this.props.readonly}
                        questionConfig={({ id: "CurrentCondition", questionText: "Current Condition/Presenting Concerns" })}
                        multiline
                    />
                    <TextQuestion
                        inline
                        readonly={this.props.readonly}
                        questionConfig={({ id: "Symptoms", questionText: "Symptoms" })}
                        multiline
                        popupContent={
                            <List bulleted>
                                <List.Item>Longevity</List.Item>
                                <List.Item>Better/Worse</List.Item>
                                <List.Item>Previous episodes</List.Item>
                            </List>
                        }
                    />
                    <TextQuestion
                        inline
                        readonly={this.props.readonly}
                        questionConfig={({ id: "PastMedicalHistory", questionText: "Past Medical History" })}
                        multiline
                    />
                    <TextQuestion
                        inline
                        readonly={this.props.readonly}
                        questionConfig={({ id: "TreatmentAndIntervention", questionText: "Treatment and intervention" })}
                        multiline
                        popupContent={
                            <List bulleted>
                                <List.Item>GP/Specialist</List.Item>
                                <List.Item>Medication</List.Item>
                            </List>
                        }
                    />
                    <TextQuestion
                        inline
                        readonly={this.props.readonly}
                        multiline
                        questionConfig={({ id: "SocialHistory", questionText: "Social History" })}
                        popupContent={
                            <List bulleted>
                                <List.Item>Family</List.Item>
                                <List.Item>Children</List.Item>
                                <List.Item>Life events</List.Item>
                                <List.Item>Financial</List.Item>
                                <List.Item>Alcohol (how much)</List.Item>
                                <List.Item>Consider assessment tools e.g. FAST</List.Item>
                                <List.Item>Smoking (how many)</List.Item>
                                <List.Item>Recreational drugs (what type / how often)</List.Item>
                                <List.Item>Hobbies</List.Item>
                                <List.Item>Exercise</List.Item>
                            </List>
                        }
                    />
                    <TextQuestion
                        inline
                        readonly={this.props.readonly}
                        multiline
                        questionConfig={({ id: "PsychologicalWellbeing", questionText: "Psychological wellbeing" })}
                        popupContent={
                            <List bulleted>
                                <List.Item>Signs of self-neglect (if f-2-f assessment)</List.Item>
                                <List.Item>Speech</List.Item>
                                <List.Item>Perception</List.Item>
                                <List.Item>Interest</List.Item>
                                <List.Item>Sleep</List.Item>
                                <List.Item>Appetite</List.Item>
                                <List.Item>Mood</List.Item>
                                <List.Item>Memory</List.Item>
                                <List.Item>Concentration</List.Item>
                                <List.Item>Activity</List.Item>
                                <List.Item>Guilt</List.Item>
                                <List.Item>Energy</List.Item>
                                <List.Item>Self-harm</List.Item>
                                <List.Item>Suicidal thoughts</List.Item>
                                <List.Item>Consider assessment tools e.g. HADS, PHQ-9</List.Item>
                            </List>
                        }
                    />
                    <TextQuestion
                        inline
                        readonly={this.props.readonly}
                        multiline
                        questionConfig={({ id: "PsychologicalRedFlags", questionText: "Psychological red flags" })}
                        popupContent={
                            <>
                                <p>If a person is experiencing several of these symptoms at one time and the symptoms are causing serious problems
                                    in the ability to study, work or relate to others, consider referral to a mental health professional.</p>
                                <List bulleted>
                                    <List.Item>Recent social withdrawal and loss of interest in others</List.Item>
                                    <List.Item>Problems with concentration, memory</List.Item>
                                    <List.Item>Increased sensitivity e.g. to sounds, smells, touch or sights.</List.Item>
                                    <List.Item>Apathy - Loss of initiative or desire to participate in any activity</List.Item>
                                    <List.Item>Unusual or exaggerated beliefs about personal powers to understand meanings or influence events;
                                        illogical or &quot;magical&quot; thinking typical of childhood in an adult</List.Item>
                                    <List.Item>Nervousness - Fear or suspiciousness of others or a strong nervous feeling</List.Item>
                                    <List.Item>Unusual behaviour - Odd, uncharacteristic, peculiar behaviour</List.Item>
                                    <List.Item>Sleep or appetite changes - Dramatic sleep and appetite changes or decline in personal care</List.Item>
                                    <List.Item>Mood changes - Rapid or dramatic shifts in feelings</List.Item>
                                </List>
                                <p>People with suicidal thoughts or intent, or thoughts of harming themselves or others need immediate attention</p>
                            </>
                        }
                    />
                    <TextQuestion
                        inline
                        readonly={this.props.readonly}
                        multiline
                        questionConfig={({ id: "WorkRelatedConcerns", questionText: "Work related concerns" })}
                        popupContent={
                            <List bulleted>
                                <List.Item>Demand</List.Item>
                                <List.Item>Control</List.Item>
                                <List.Item>Support</List.Item>
                                <List.Item>Role</List.Item>
                                <List.Item>Relationship</List.Item>
                                <List.Item>Change</List.Item>
                            </List>
                        }
                    />
                    <TextQuestion
                        inline
                        readonly={this.props.readonly}
                        multiline
                        questionConfig={({ id: "FunctionalCapacity", questionText: "Functional capacity" })}
                        popupContent={
                            <List bulleted>
                                <List.Item>Mobility</List.Item>
                                <List.Item>Pain</List.Item>
                                <List.Item>Stiffness</List.Item>
                                <List.Item>Pushing</List.Item>
                                <List.Item>Pulling</List.Item>
                                <List.Item>Locking</List.Item>
                                <List.Item>Swelling</List.Item>
                                <List.Item>Deformity</List.Item>
                                <List.Item>Weakness</List.Item>
                                <List.Item>Walk</List.Item>
                                <List.Item>Stand</List.Item>
                                <List.Item>Sit</List.Item>
                                <List.Item>Crouch</List.Item>
                                <List.Item>Kneel</List.Item>
                                <List.Item>Stairs</List.Item>
                                <List.Item>Grip</List.Item>
                                <List.Item>Sensory disturbance</List.Item>
                                <List.Item>Aggravating and relieving factors</List.Item>
                                <List.Item>Continence</List.Item>
                                <List.Item>Physical co-ordination</List.Item>
                                <List.Item>Manual dexterity</List.Item>
                                <List.Item>Ability to lift</List.Item>
                                <List.Item>Memory/understanding/concentration</List.Item>
                            </List>
                        }
                    />
                    <TextQuestion
                        inline
                        readonly={this.props.readonly}
                        multiline
                        questionConfig={({ id: "MusculoSkeletalRedFlags", questionText: "Musculo-skeletal Red Flags" })}
                        popupContent={
                            <p>Thoracic pain, age of onset less than 20 or more than 55 years, loss of control of the bowel or bladder,
                            weakness or numbness in a leg or arm, foot drop, disturbed gait, high fever., saddle anaesthesia
                                (numbness of the anus, perineum or genitals)., history of carcinoma, structural deformity.</p>
                        }
                    />
                    <TextQuestion
                        inline
                        readonly={this.props.readonly}
                        multiline
                        questionConfig={({ id: "PrognosisAndOnwardManagement", questionText: "Prognosis & Onward Management" })}
                        popupContent={
                            <List bulleted>
                                <List.Item>FME: GP / Spec / Other</List.Item>
                                <List.Item>Further Review</List.Item>
                                <List.Item>OHA: Tel / F-2-f</List.Item>
                                <List.Item>OHP</List.Item>
                                <List.Item>Physio</List.Item>
                                <List.Item>CBT</List.Item>
                                <List.Item>Counselling</List.Item>
                            </List>
                        }
                    />
                    <TextQuestion
                        inline
                        readonly={this.props.readonly}
                        questionConfig={({ id: "FitnessForWork", questionText: "Fitness for work" })}
                        multiline
                    />
                    <OldDateQuestion
                        inline
                        readonly={this.props.readonly}
                        questionConfig={({ id: "ReturnToWorkDate", questionText: "Return to work date / estimated return to work date" })}
                    />
                    <TextQuestion
                        inline
                        readonly={this.props.readonly}
                        questionConfig={({ id: "ReturnToWorkPlan", questionText: "Recommendations / Return to work plan" })}
                        multiline
                    />
                    <DropdownQuestion
                        inline
                        readonly={this.props.readonly}
                        questionConfig={({ id: "MedicalClassification", questionText: "Medical Classification" })}
                        options={ohaMedicalClassificationOptions.sort((a, b) => a.text.localeCompare(b.text))}
                        mandatory
                    />
                    {medicalClassificationSubCategoryOptions[this.getAnswerToQuestionWithId("MedicalClassification") as string]
                        && <DropdownQuestion
                            inline
                            readonly={this.props.readonly}
                            questionConfig={({ id: "MedicalClassificationSubCategory", questionText: "Classification Sub Category" })}
                            mandatory
                            options={medicalClassificationSubCategoryOptions[this.getAnswerToQuestionWithId("MedicalClassification") as string]
                                .sort((a, b) => a.text.localeCompare(b.text))}
                        />}
                    <TextQuestion
                        inline
                        readonly={this.props.readonly}
                        questionConfig={({ id: "EqualityAct", questionText: "Equality Act 2010 (Explain Rationale)" })}
                        multiline
                    />
                </AccordionQuestionnaireSection>
                <AccordionQuestionnaireSection
                    title="Consent to Release the Report"
                    id={consentToReleaseReportSectionId}
                    index={this.props.index + 1}
                >
                    <p>Please clearly outline the content of your report to the employee and then ask them confirm their preference for consent</p>
                    <p><strong>Note:</strong> Once your report has been written it will be managed based on the choice that you make below</p>
                    <RadioButtonQuestion
                        hideDivider={!!this.getAnswerToQuestionWithId("ScopeOfReport")}
                        inline
                        readonly={this.props.readonly}
                        questionConfig={({ id: "ScopeOfReport", questionText: "Consent for release of Report" })}
                        mandatory
                    >
                        <RadioButton label="I would like to see a copy of my report before it is released to HR and my Manager" value="CopyRequired" />
                        <RadioButton label="I am happy for the report to be sent to me, HR and my Manager at the same time" value="CopyRequiredSameTime" />
                        <RadioButton
                            // eslint-disable-next-line max-len
                            label="Neither of the above (choose this option if any of the email addresses below are wrong or if the employee / manager email address is blank)"
                            value="AlternativeScope"
                        />
                    </RadioButtonQuestion>
                    {
                        (() => {
                            switch (this.getAnswerToQuestionWithId("ScopeOfReport")) {
                                case "CopyRequired":
                                    // eslint-disable-next-line max-len
                                    return <Segment>If the employee chooses to see the report first you must notify them that the report will be sent to the Optima Health portal and they will receive an email notification telling them that the report is ready. Advise them that they <strong>must</strong> respond to this within 5 Days or the report will be automatically sent to their Manager and HR. <strong>Check the email address of the manager and employee are correct below. If not, or the employee / manager email address is blank you must select the third option (Neither of the above)</strong></Segment>;
                                case "CopyRequiredSameTime":
                                    // eslint-disable-next-line max-len
                                    return <Segment>Advise the employee that they will receive a copy of the report via the Optima Health portal and that they will receive an email notification telling them the report is ready. <strong>Check the email address of the manager and employee are correct below. If not, or the employee / manager email address is blank you must select the third option (Neither of the above)</strong></Segment>;
                                case "AlternativeScope":
                                    return (<TextQuestion
                                        inline
                                        mandatory
                                        readonly={this.props.readonly}
                                        questionConfig={({ id: "AlternativeConsentChoice", questionText: "Details of Alternative Consent choice" })}
                                        multiline
                                    />);
                                default:
                                    return null;
                            }
                        })()
                    }
                    <TextQuestion
                        inline
                        readonly
                        questionConfig={({ id: employeeEmailAddressQuestionId, questionText: "Employee Email Address" })}
                        answer={this.props.ohCase?.personalEmailAddress}
                    />
                    <TextQuestion
                        inline
                        readonly
                        questionConfig={({ id: managerEmailAddressQuestionId, questionText: "Manager Email Address" })}
                        answer={this.props.consultationDetails && this.props.consultationDetails.lineManagersEmails.join(" & ")}
                    />
                    {this.props.consultationDetails && this.props.consultationDetails.hrEmails &&
                        <TextQuestion
                            inline
                            readonly
                            questionConfig={({ id: hrEmailAddressQuestionId, questionText: "HR Email Address" })}
                            answer={this.props.consultationDetails && this.props.consultationDetails.hrEmails.join(" & ")}
                        />
                    }
                </AccordionQuestionnaireSection>
            </>
        );
    }

    private getAnswerToQuestionWithId(id: string) {
        return this.props.questions?.find(question => question.id === id)?.answer;
    }
}

const mapStateToProps = (state: ApplicationState) => {
    const taskId = taskIdSelector(state);
    return { consultationDetails: state.consultationDetails.find(r => r.consultationTaskId === taskId), taskId };
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
    loadConsultationDetails: (ohCaseId: string, taskId: string) => dispatch(loadConsultationDetails({ ohCaseId, taskId })),
    updateAnswer: payload => dispatch(updateQuestion({ ...payload, questionnaireKey })),
    clearQuestionnaire: () => dispatch(clearQuestionnaire(questionnaireKey))
});

export const OldReferralClinicalNotesSections = connect(mapStateToProps, mapDispatchToProps)(OldReferralClinicalNotesSectionsUnconnected);
